import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// ---------- Subscriptions ----------
export const AdminSubscriptions = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/admin/subscriptions').then((r) => setData(r.data)).catch((e) =>
      toast.error(e.response?.data?.detail || 'Failed'));
  }, []);
  return (
    <div>
      <h1 className="font-serif text-3xl text-zita-espresso mb-6">Subscriptions</h1>
      {!data && <div className="text-sm text-zita-mocha">Loading…</div>}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="admin-subs-grid">
          {Object.entries(data.by_status || {}).map(([k, v]) => (
            <div key={k} className="zita-card p-5" data-testid={`admin-sub-${k}`}>
              <div className="text-xs uppercase tracking-wider text-zita-mocha mb-1">{k.replace('_', ' ')}</div>
              <div className="text-3xl font-serif text-zita-espresso">{v}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Analytics ----------
export const AdminAnalytics = () => {
  const [rev, setRev] = useState(null);
  const [ai, setAi] = useState(null);
  const [scan, setScan] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get('/admin/analytics/revenue').then((r) => setRev(r.data)).catch(() => {}),
      api.get('/admin/analytics/ai').then((r) => setAi(r.data)).catch(() => {}),
      api.get('/admin/analytics/scans').then((r) => setScan(r.data)).catch(() => {}),
    ]);
  }, []);
  return (
    <div>
      <h1 className="font-serif text-3xl text-zita-espresso mb-6">Analytics</h1>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="zita-card p-5">
          <div className="text-xs uppercase tracking-wider text-zita-mocha mb-2">Revenue</div>
          {rev ? (
            <>
              <div className="text-2xl font-serif text-zita-espresso">
                ${rev.estimated_monthly_run_rate_usd?.toLocaleString?.() ?? '—'} <span className="text-xs text-zita-mocha">/mo est</span>
              </div>
              <div className="text-xs text-zita-mocha mt-2">
                Active {rev.active} · Trialing {rev.trialing} · Past-due {rev.past_due} · Canceled {rev.canceled}
              </div>
              <div className="text-[10px] text-zita-muted mt-2">{rev.note}</div>
            </>
          ) : <div className="text-sm text-zita-mocha">Loading…</div>}
        </div>
        <div className="zita-card p-5">
          <div className="text-xs uppercase tracking-wider text-zita-mocha mb-2">Ari activity</div>
          {ai ? (
            <>
              <div className="text-2xl font-serif text-zita-espresso">{ai.total_chat_messages}</div>
              <div className="text-xs text-zita-mocha mt-1">total chat messages</div>
              <div className="text-xs text-zita-cocoa mt-2">+{ai.chat_messages_today} today</div>
            </>
          ) : <div className="text-sm text-zita-mocha">Loading…</div>}
        </div>
        <div className="zita-card p-5">
          <div className="text-xs uppercase tracking-wider text-zita-mocha mb-2">Scans</div>
          {scan ? (
            <>
              <div className="text-2xl font-serif text-zita-espresso">{scan.total_scans}</div>
              <div className="text-xs text-zita-mocha mt-1">total scans · {scan.total_listings} listings</div>
              <div className="text-xs text-zita-cocoa mt-2">
                Running {scan.running_scans} · Complete {scan.complete_scans} · Failed {scan.failed_scans}
              </div>
            </>
          ) : <div className="text-sm text-zita-mocha">Loading…</div>}
        </div>
      </div>
    </div>
  );
};

// ---------- Marketplaces ----------
export const AdminMarketplaces = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get('/admin/marketplaces').then((r) => setItems(r.data.items || [])); }, []);
  return (
    <div>
      <h1 className="font-serif text-3xl text-zita-espresso mb-6">Marketplaces</h1>
      <div className="zita-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zita-sand/50 text-zita-mocha uppercase tracking-wider text-[11px]">
            <tr>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-left px-4 py-3">Connected users</th>
              <th className="text-left px-4 py-3">Last success</th>
              <th className="text-left px-4 py-3">Errors</th>
            </tr>
          </thead>
          <tbody data-testid="admin-marketplaces-tbody">
            {!items.length && <tr><td colSpan="4" className="px-4 py-6 text-center text-zita-mocha">No connected marketplaces yet.</td></tr>}
            {items.map((m) => (
              <tr key={m.source_key} className="border-t border-zita-taupe/40">
                <td className="px-4 py-3 font-medium text-zita-espresso">{m.source_key}</td>
                <td className="px-4 py-3 text-zita-mocha">{m.connected_users}</td>
                <td className="px-4 py-3 text-xs text-zita-mocha">{m.last_success_at ? new Date(m.last_success_at).toLocaleString() : '—'}</td>
                <td className="px-4 py-3 text-zita-mocha">{m.errors}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Errors ----------
export const AdminErrors = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/admin/errors').then((r) => setData(r.data)); }, []);
  return (
    <div>
      <h1 className="font-serif text-3xl text-zita-espresso mb-6">Errors</h1>
      {!data && <div className="text-sm text-zita-mocha">Loading…</div>}
      {data && (
        <div className="space-y-6" data-testid="admin-errors">
          <div>
            <div className="text-xs uppercase tracking-wider text-zita-mocha mb-2">Failed scans</div>
            <div className="zita-card divide-y divide-zita-taupe/40">
              {!data.scan_failures?.length && <div className="p-4 text-sm text-zita-mocha">No failed scans.</div>}
              {(data.scan_failures || []).map((e) => (
                <div key={e.scan_id} className="p-3 text-sm">
                  <div className="font-medium text-zita-espresso">scan {e.scan_id}</div>
                  <div className="text-xs text-zita-mocha">user {e.user_id} · {e.failures} failures · {new Date(e.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-zita-mocha mb-2">Critical tickets</div>
            <div className="zita-card divide-y divide-zita-taupe/40">
              {!data.critical_tickets?.length && <div className="p-4 text-sm text-zita-mocha">No critical tickets.</div>}
              {(data.critical_tickets || []).map((t) => (
                <div key={t.ticket_id} className="p-3 text-sm">
                  <div className="font-medium text-zita-espresso">{t.subject}</div>
                  <div className="text-xs text-zita-mocha">ticket {t.ticket_id} · user {t.user_id} · {new Date(t.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- System ----------
export const AdminSystem = () => {
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    api.get('/admin/system/health').then((r) => setData(r.data));
    api.get('/admin/jobs').then((r) => setJobs(r.data));
  }, []);
  return (
    <div>
      <h1 className="font-serif text-3xl text-zita-espresso mb-6">System</h1>
      {!data && <div className="text-sm text-zita-mocha">Loading…</div>}
      {data && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="zita-card p-5" data-testid="admin-system-health">
            <div className="text-xs uppercase tracking-wider text-zita-mocha mb-3">Health</div>
            <div className="text-sm space-y-2">
              <div>Database: <span className={data.database.ok ? 'text-emerald-700' : 'text-red-700'}>{data.database.ok ? 'ok' : 'DOWN'}</span></div>
              <div>API: <span className="text-emerald-700">ok</span></div>
              <div className="text-xs text-zita-mocha">Snapshot {new Date(data.timestamp).toLocaleTimeString()}</div>
            </div>
          </div>
          <div className="zita-card p-5">
            <div className="text-xs uppercase tracking-wider text-zita-mocha mb-3">Background jobs</div>
            {jobs && (
              <div className="text-sm">
                <div>Running: {jobs.running}</div>
                <div>Queued: {jobs.queued}</div>
                <div className="text-[11px] text-zita-muted mt-2">{jobs.note}</div>
              </div>
            )}
          </div>
          <div className="zita-card p-5 md:col-span-2">
            <div className="text-xs uppercase tracking-wider text-zita-mocha mb-3">Collections</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {Object.entries(data.collections || {}).map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between border-b border-zita-taupe/40 py-1">
                  <span className="text-zita-mocha">{k}</span>
                  <span className="font-medium text-zita-espresso">{v ?? '—'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
